import {
  type GcpSqlVmBackupSettings,
  GcpSqlVmSetupPath,
} from '@repo/api-gw-sdk';

const MIB = 1024 * 1024;

// The values the setup script runs with when Eon sets none. A customer who ran it with other arguments has those
// instead, which Eon can't see, so the console calls an unset value the setup value and quotes these as the default.
export const SETUP_DEFAULTS = {
  maxTransferSizeMiB: 20,
  logBackupIntervalMinutes: 5,
  fullBackupTime: '01:00:00',
};

export interface GcpSqlVmBackupSettingsForm {
  maxTransferSizeMiB: string;
  logBackupIntervalMinutes: string;
  fullBackupTime: string;
}

const FULL_BACKUP_TIME = /^([01]\d|2[0-3]):[0-5]\d:[0-5]\d$/;

const inRange = (value: string, min: number, max: number) =>
  value === '' ||
  (/^\d+$/.test(value) && Number(value) >= min && Number(value) <= max);

// The control takes multiples of 64 KiB, i.e. 1/16 MiB, so an operator-set value can show as a decimal such as 5.5.
const PARTS_PER_MIB = 16;

const isPartSize = (value: string) =>
  value === '' ||
  (/^\d+(\.\d+)?$/.test(value) &&
    Number.isInteger(Number(value) * PARTS_PER_MIB) &&
    Number(value) >= 5 &&
    Number(value) <= 20);

export const settingsValidation = {
  maxTransferSizeMiB: {
    validateInput: isPartSize,
    errorMessage: () => 'Enter a size from 5 to 20 MiB, in steps of 1/16 MiB.',
  },
  logBackupIntervalMinutes: {
    validateInput: (value: string) => inRange(value, 1, 10),
    errorMessage: () => 'Enter a number of minutes from 1 to 10.',
  },
  fullBackupTime: {
    validateInput: (value: string) =>
      value === '' || FULL_BACKUP_TIME.test(value),
    errorMessage: () => 'Enter a time as HH:mm:ss, for example 01:00:00.',
  },
};

export const isFormValid = (
  form: GcpSqlVmBackupSettingsForm,
  { includePartSize }: { includePartSize: boolean }
) =>
  (Object.keys(settingsValidation) as (keyof GcpSqlVmBackupSettingsForm)[])
    .filter((key) => includePartSize || key !== 'maxTransferSizeMiB')
    .every((key) => settingsValidation[key].validateInput(form[key]));

export const formOf = (
  settings: GcpSqlVmBackupSettings | undefined
): GcpSqlVmBackupSettingsForm => ({
  maxTransferSizeMiB: settings?.maxTransferSizeBytes
    ? String(settings.maxTransferSizeBytes / MIB)
    : '',
  logBackupIntervalMinutes: settings?.logBackupIntervalMinutes
    ? String(settings.logBackupIntervalMinutes)
    : '',
  fullBackupTime: settings?.fullBackupTime ?? '',
});

// A field in the form is always sent, an empty one as 0 or "", which the server reads as "return to the setup value", so
// clearing a field is how a customer undoes a change. The part size is left out while it is disabled, keeping what is stored.
export const updateOf = (
  form: GcpSqlVmBackupSettingsForm,
  { includePartSize }: { includePartSize: boolean }
): GcpSqlVmBackupSettings => ({
  ...(includePartSize && {
    maxTransferSizeBytes: form.maxTransferSizeMiB
      ? Number(form.maxTransferSizeMiB) * MIB
      : 0,
  }),
  logBackupIntervalMinutes: form.logBackupIntervalMinutes
    ? Number(form.logBackupIntervalMinutes)
    : 0,
  fullBackupTime: form.fullBackupTime,
});

// Only SQL Server 2022 and later back up straight to object storage, the one path the part size applies to.
export const partSizeDisabledReason = (
  setupPath: GcpSqlVmSetupPath | undefined
) => {
  if (setupPath === GcpSqlVmSetupPath.GCP_SQL_VM_SETUP_PATH_OBJECT_STORAGE) {
    return undefined;
  }
  return setupPath === GcpSqlVmSetupPath.GCP_SQL_VM_SETUP_PATH_DISK
    ? 'Applies only to SQL Server 2022 and later.'
    : "Available once Eon detects the VM's SQL Server version.";
};

export const settingsSummary = (
  settings: GcpSqlVmBackupSettings | undefined
) => {
  const interval =
    settings?.logBackupIntervalMinutes ??
    SETUP_DEFAULTS.logBackupIntervalMinutes;
  const fullAt = settings?.fullBackupTime ?? SETUP_DEFAULTS.fullBackupTime;
  const isCustom = Object.values(settings ?? {}).some(
    (value) => value !== undefined && value !== null
  );
  return `Log backups every ${interval} min, full backup daily at ${fullAt}${isCustom ? '' : ' (setup values)'}`;
};
